import { ArrowUturnLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import { getBlockchainsOptions } from "@src/components/DefuseSDK/constants/blockchains"
import { assetNetworkAdapter } from "@src/components/DefuseSDK/utils/adapters"
import { Dialog } from "@src/components/Dialog"
import ErrorMessage from "@src/components/ErrorMessage"
import { ContactsIcon, SendIcon } from "@src/icons"
import { midTruncate } from "@src/shared/utils/display"
import type React from "react"
import type { SupportedChainName, TokenInfo } from "../../types/base"

export type PostScanActionSheetProps = {
  open: boolean
  address: string
  chain: SupportedChainName
  sendToken: TokenInfo | null
  isSendPending: boolean
  sendError: string | null
  onSend: () => void
  onSaveContact: () => void
  onCancel: () => void
}

function labelForChain(chain: SupportedChainName): string {
  return getBlockchainsOptions()[assetNetworkAdapter[chain]]?.label ?? chain
}

export function PostScanActionSheet({
  open,
  address,
  chain,
  sendToken,
  isSendPending,
  sendError,
  onSend,
  onSaveContact,
  onCancel,
}: PostScanActionSheetProps): React.JSX.Element {
  const chainLabel = labelForChain(chain)
  // Send row shows only when the scan resolved to a concrete token.
  const showSendRow = sendToken != null

  return (
    <Dialog
      title="Address scanned"
      open={open}
      onClose={onCancel}
      bgClassName="bg-gray-25 dark:bg-gray-850"
    >
      <div className="-outline-offset-1 flex flex-col items-center justify-center rounded-2xl bg-gray-100 p-3 outline outline-gray-200 dark:bg-gray-900 dark:outline-white/5">
        <div className="text-center font-mono font-semibold text-gray-900 text-sm/5 dark:text-green-500">
          {midTruncate(address, 18)}
        </div>
        <div className="font-semibold text-gray-500 text-sm/5 dark:text-white">
          {chainLabel}
        </div>
      </div>

      <div className="mt-6 space-y-1">
        {showSendRow && (
          <button
            type="button"
            disabled={isSendPending}
            onClick={onSend}
            className="-outline-offset-1 flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-4 text-left outline outline-gray-200 dark:bg-gray-800 dark:outline-white/5"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <SendIcon className="size-4.5 text-gray-700 dark:text-gray-300" />
            </div>
            <span className="flex-1">
              <span className="block font-semibold text-gray-900 text-sm/5 dark:text-white">
                {isSendPending
                  ? "Checking balance…"
                  : sendToken
                    ? `Send ${sendToken.symbol} on ${chainLabel}`
                    : "Send to this address"}
              </span>
              <span className="block font-medium text-gray-500 text-xs/4 dark:text-gray-400">
                Pay with your assets now
              </span>
            </span>
            <ChevronRightIcon className="size-5 shrink-0 text-gray-700 dark:text-gray-300" />
          </button>
        )}

        {sendError && (
          <ErrorMessage className="mt-1 mb-3 text-center">
            {sendError}
          </ErrorMessage>
        )}

        <button
          type="button"
          onClick={onSaveContact}
          className="-outline-offset-1 flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-4 text-left outline outline-gray-200 dark:bg-gray-800 dark:outline-white/5"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <ContactsIcon className="size-4.5 text-gray-700 dark:text-gray-300" />
          </div>
          <span className="flex-1">
            <span className="block font-semibold text-gray-900 text-sm/5 dark:text-white">
              Save as a contact
            </span>
            <span className="block font-medium text-gray-500 text-xs/4 dark:text-gray-400">
              Add address to your contacts
            </span>
          </span>
          <ChevronRightIcon className="size-5 shrink-0 text-gray-700 dark:text-gray-300" />
        </button>
      </div>

      <Button
        size="xl"
        variant="secondary"
        fullWidth
        onClick={onCancel}
        className="mt-6"
      >
        <ArrowUturnLeftIcon className="size-4.5 shrink-0" />
        <span>Cancel</span>
      </Button>
    </Dialog>
  )
}
